import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnDestroy,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { LocationService, LocationResult } from '../services/location.service';

export interface LocationSelectedEvent {
  formatted: string;
  display_name: string;
  city?: string;
  country?: string;
}

@Component({
  selector: 'app-location-autocomplete',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationAutocompleteComponent),
      multi: true,
    },
  ],
  template: `
    <div class="location-autocomplete">
      <input
        type="text"
        [formControl]="searchControl"
        [placeholder]="placeholder"
        class="location-input"
        [class.invalid]="inputClass.includes('invalid')"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (input)="onInput()"
        autocomplete="off"
      />

      <div
        class="location-dropdown"
        *ngIf="showDropdown && (searchResults.length > 0 || isSearching)"
      >
        <div class="search-status" *ngIf="isSearching">
          <div class="spinner"></div>
          <span>Searching locations...</span>
        </div>

        <div
          class="no-results"
          *ngIf="
            !isSearching &&
            searchResults.length === 0 &&
            searchControl.value &&
            searchControl.value.length >= 3
          "
        >
          No locations found for "{{ searchControl.value }}"
        </div>

        <div
          class="location-results"
          *ngIf="!isSearching && searchResults.length > 0"
        >
          <div
            *ngFor="let result of searchResults; let i = index"
            class="location-item"
            [class.highlighted]="i === highlightedIndex"
            (click)="selectLocation(result)"
            (mouseenter)="highlightedIndex = i"
          >
            <div class="location-primary">{{ result.formatted }}</div>
            <div
              class="location-secondary"
              *ngIf="result.display_name !== result.formatted"
            >
              {{ result.display_name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./location-autocomplete.component.css'],
})
export class LocationAutocompleteComponent
  implements ControlValueAccessor, OnDestroy
{
  @Input() placeholder = 'Start typing a city name...';
  @Input() inputClass = '';
  @Output() locationSelected = new EventEmitter<LocationResult>();
  @Output() locationSelectedWithCoords =
    new EventEmitter<LocationSelectedEvent>();

  searchControl = new FormControl('');
  searchResults: LocationResult[] = [];
  isSearching = false;
  showDropdown = false;
  highlightedIndex = -1;

  private searchSubject = new Subject<string>();
  private subscription: Subscription;
  private onChange = (value: string) => {};
  private onTouched = () => {};
  private userHasInteracted = false;

  constructor(private locationService: LocationService) {
    this.subscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query && query.length >= 3 && this.userHasInteracted) {
            this.isSearching = true;
            return this.locationService.searchLocations(query);
          } else {
            this.searchResults = [];
            this.isSearching = false;
            return of([]);
          }
        })
      )
      .subscribe((results) => {
        this.searchResults = results;
        this.isSearching = false;
        this.highlightedIndex = -1;
      });

    this.searchControl.valueChanges.subscribe((value) => {
      this.searchSubject.next(value || '');
      this.onChange(value || '');

      if (value && value.length >= 3 && this.userHasInteracted) {
        this.showDropdown = true;
      } else if (!this.userHasInteracted) {
        this.showDropdown = false;
        this.searchResults = [];
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  writeValue(value: string): void {
    this.searchControl.setValue(value, { emitEvent: false });
    this.userHasInteracted = false;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  onInput(): void {
    this.userHasInteracted = true;
  }

  onFocus(): void {
    this.userHasInteracted = true;
    if (this.searchControl.value && this.searchControl.value.length >= 3) {
      this.showDropdown = true;
      this.searchSubject.next(this.searchControl.value);
    }
  }

  onBlur(): void {
    setTimeout(() => {
      this.showDropdown = false;
      this.onTouched();
    }, 200);
  }

  selectLocation(location: LocationResult): void {
    this.searchControl.setValue(location.formatted, { emitEvent: false });
    this.onChange(location.formatted);
    this.locationSelected.emit(location);

    const locationEvent: LocationSelectedEvent = {
      formatted: location.formatted,
      display_name: location.display_name,
      city: location.city,
      country: location.country,
    };

    this.locationSelectedWithCoords.emit(locationEvent);

    this.showDropdown = false;
    this.searchResults = [];
    this.userHasInteracted = false;
  }
}
